import { useEffect } from 'react';
import SEO from '../../../components/SEO';

export default function CommercialSecurityMississauga() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const faqs = [
    {
      question: "Does Mississauga require alarm permits for commercial security systems?",
      answer: "Yes. The City of Mississauga requires commercial alarm permits under By-law 0394-2003. Annual fees range from $57 to $97 depending on occupancy type. Unpermitted alarms are subject to fines and may result in delayed Peel Regional Police response. Alliance Security handles all permit applications, renewals, and compliance documentation on behalf of our Mississauga commercial clients."
    },
    {
      question: "What security measures do GTAA cargo corridor businesses need?",
      answer: "Businesses in the Pearson Airport cargo corridor — Airport Road, Derry Road East, Convair Drive — face the highest air cargo theft rates in Canada. Effective security requires multi-zone perimeter cameras with LPR coverage of all dock and gate access points, 24/7 ULC-certified monitoring with sub-5-minute video verification response, dock door access control with timestamped audit logs, and Transport Canada-compliant documentation for cargo insurance purposes. We have installed systems in 30+ GTAA corridor facilities."
    },
    {
      question: "How much does commercial security cost in Mississauga?",
      answer: "Mississauga commercial security ranges from $2,400–$8,500 for installation plus $45–$145/month for ULC-certified monitoring. Square One and Heartland retail stores average $3,000–$5,500. Airport corridor warehouse and logistics facilities typically invest $6,000–$12,000 for comprehensive perimeter protection, dock access control, and cargo monitoring. Most clients recover installation costs within 14 months through insurance premium reductions of 18–28%."
    }
  ];

  const neighborhoods = [
    "Airport Corporate Centre",
    "GTAA Cargo Zone",
    "Meadowvale Business Park",
    "Square One District",
    "Heartland Town Centre",
    "Highway 401 Corridor",
    "Derry Road East Industrial",
    "Dixie Road Industrial",
    "Port Credit",
    "Streetsville",
    "Erin Mills",
    "Clarkson"
  ];

  const otherCities = [
    { name: "Toronto", slug: "toronto" },
    { name: "Brampton", slug: "brampton" },
    { name: "Oakville", slug: "oakville" },
    { name: "Hamilton", slug: "hamilton" },
    { name: "Richmond Hill", slug: "richmond-hill" },
    { name: "Vaughan", slug: "vaughan" }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://alliancesecuritysystems.com/commercial-security/mississauga#business",
        "name": "Alliance Security Systems - Commercial Security Mississauga",
        "image": "https://public.readdy.ai/ai/img_res/c3bb27f2bbafc854b77402724a6a7b56.jpg",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Mississauga",
          "addressRegion": "ON",
          "addressCountry": "CA"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "43.5890",
          "longitude": "-79.6441"
        },
        "url": "https://alliancesecuritysystems.com/commercial-security/mississauga",
        "telephone": "+1-888-458-9181",
        "priceRange": "$$-$$$",
        "areaServed": {
          "@type": "City",
          "name": "Mississauga"
        },
        "serviceType": ["Commercial Security Systems", "Business Alarm Systems", "Commercial CCTV", "Access Control Systems", "Cargo Security", "Retail Loss Prevention"]
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://alliancesecuritysystems.com/commercial-security/mississauga#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://alliancesecuritysystems.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Commercial Security",
            "item": "https://alliancesecuritysystems.com/commercial-security"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Mississauga",
            "item": "https://alliancesecuritysystems.com/commercial-security/mississauga"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": "https://alliancesecuritysystems.com/commercial-security/mississauga#faq",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <>
      <SEO
        title="Commercial Security Company Mississauga | Cargo, Industrial & Retail Systems"
        description="Best commercial security company in Mississauga. ULC-certified systems for Pearson Airport cargo corridor, Highway 401 industrial belt & Square One retail. Trusted by 150+ Mississauga businesses. Free assessment."
        keywords="commercial security Mississauga, business security Mississauga, cargo security GTAA, commercial CCTV Mississauga, access control Mississauga, Peel Region commercial security"
        canonical="/commercial-security/mississauga"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-white">

        {/* Hero Section */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Mississauga%20Pearson%20Airport%20cargo%20corridor%20industrial%20zone%20modern%20warehouse%20facilities%20aerial%20twilight%20view%20Peel%20Region%20commercial%20district%20Ontario%20logistics%20hub%20professional%20photography&width=1920&height=800&seq=comm-mississauga-hero-v2&orientation=landscape"
            alt="Commercial Security Mississauga - Pearson Airport Cargo and Industrial Districts"
            title="Mississauga Commercial Security Systems"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center text-white">
            <nav className="flex items-center justify-center gap-2 text-sm mb-6" itemScope itemType="https://schema.org/BreadcrumbList">
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="/" itemProp="item" className="hover:text-teal-400 transition-colors">
                  <span itemProp="name">Home</span>
                </a>
                <meta itemProp="position" content="1" />
              </span>
              <span className="text-white/60">/</span>
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="/commercial-security" itemProp="item" className="hover:text-teal-400 transition-colors">
                  <span itemProp="name">Commercial Security</span>
                </a>
                <meta itemProp="position" content="2" />
              </span>
              <span className="text-white/60">/</span>
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span itemProp="name" className="text-teal-400">Mississauga</span>
                <meta itemProp="position" content="3" />
              </span>
            </nav>

            <h1 className="text-5xl font-bold mb-6">Commercial Security Mississauga</h1>
            <p className="text-xl mb-8 text-white/90">
              Canada&apos;s Air Cargo Capital — Industrial-Grade Security for the Pearson Corridor, Highway 401 Belt &amp; Square One Retail
            </p>
            <button
              onClick={scrollToForm}
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              Get Free Security Assessment
            </button>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-gray-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">150+</div>
                <div className="text-sm text-gray-300">Mississauga Commercial Clients</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">&lt;4 min</div>
                <div className="text-sm text-gray-300">Average Response Time</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">20+ yrs</div>
                <div className="text-sm text-gray-300">Serving Mississauga Businesses</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">23%</div>
                <div className="text-sm text-gray-300">Avg Insurance Discount</div>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Security Challenges */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Commercial Security Challenges in Mississauga</h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Mississauga&apos;s three commercial zones — the Pearson Airport cargo corridor, the Highway 401 industrial belt, and the Square One retail concentration — each carry a distinct crime profile that demands purpose-built security, not off-the-shelf systems
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Pearson Cargo Corridor */}
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-plane-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Pearson Airport Cargo Corridor</h3>
                <p className="text-gray-600 mb-4">
                  The GTAA cargo zone — Airport Road, Derry Road East, and Convair Drive — records Canada&apos;s highest concentration of air cargo theft per cargo throughput unit. Freight forwarders, bonded warehouses, and courier hubs along this corridor are targeted by organized theft rings that exploit dock access gaps, after-hours staging operations, and inadequate gate camera coverage. Standard motion detection is useless against crews that operate within normal freight windows.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>PRP Commercial Crime Unit:</strong> GTAA corridor properties record 3.2x the national average for air cargo theft — the highest concentration in Canada
                </div>
              </div>

              {/* Highway 401 Industrial Belt */}
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-road-map-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Highway 401 Industrial Belt</h3>
                <p className="text-gray-600 mb-4">
                  The 12-kilometre industrial corridor from Dixie Road to Mavis Road — Meadowvale Business Park, Heartland industrial lands, and the distribution centre cluster along Derry Road East — sees elevated commercial break-in rates driven by rapid highway egress access. New-tenant properties in the 401 belt are the highest-risk cohort: unfamiliar with local crime patterns, often operating with inadequate dock access controls, and underestimating perimeter camera coverage requirements.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>PRP data:</strong> 62% of after-hours 401 belt break-ins are enabled by unrestricted dock door access — the same entry point left unaddressed by most generic alarm installs
                </div>
              </div>

              {/* Square One / Heartland ORC */}
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-store-3-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Square One &amp; Heartland ORC</h3>
                <p className="text-gray-600 mb-4">
                  Square One and Heartland Town Centre together account for the highest organized retail crime (ORC) loss concentration in Ontario outside of Toronto — an estimated $6.8M in annual ORC-attributed losses. ORC rings operating at Square One use coordinated distraction, multi-point simultaneous entry, and counter-surveillance tradecraft. Standard EAS tags and motion-activated cameras do not disrupt pre-entry reconnaissance. LPR coverage of retail parkades and facial recognition at high-risk entrances are the only countermeasures with documented ORC deterrence.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>Retail Council of Canada:</strong> Square One and Heartland ORC losses represent Ontario&apos;s densest retail loss concentration outside Toronto&apos;s core
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Peel Region Is Different */}
        <section className="py-16 bg-teal-50">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="flex-shrink-0 w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center">
                <i className="ri-shield-star-line text-3xl text-white"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-3">Why Peel Regional Police Coverage Matters to Your Security Design</h2>
                <p className="text-gray-700 leading-relaxed">
                  Mississauga is served by <strong>Peel Regional Police</strong> — a regional force covering Mississauga, Brampton, and Caledon under a unified dispatch model. Unlike a standalone municipal force, PRP operates with regional response zone prioritization, which means response times and priority call routing differ meaningfully from Toronto Police Service or Hamilton Police Service. Security providers who design systems based on Toronto or Hamilton response assumptions will miscalculate verification window requirements. Our Mississauga commercial systems are calibrated to PRP&apos;s specific dispatch protocols and Commercial Crime Unit reporting procedures — including GTAA corridor cargo incident documentation requirements.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Commercial Security Solutions for Mississauga</h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Purpose-built for the Pearson cargo corridor, 401 industrial belt, and Square One retail — not repurposed residential systems
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <a href="/camera-packages" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-camera-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Commercial Cameras</h3>
                <p className="text-gray-600 mb-4">4K resolution, LPR for dock and gate coverage, thermal imaging, AI perimeter analytics, facial recognition for retail ORC deterrence</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/alarm-packages" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-alarm-warning-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Business Alarms</h3>
                <p className="text-gray-600 mb-4">ULC-certified monitoring, perimeter sensors, glass break detection, panic buttons, direct Peel Regional Police dispatch integration</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/access-control" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-key-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Access Control</h3>
                <p className="text-gray-600 mb-4">Dock door control with timestamped audit logs, biometric readers, role-based credentials, visitor management — Transport Canada documentation-ready</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/live-monitoring" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-eye-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Live Monitoring</h3>
                <p className="text-gray-600 mb-4">24/7 video verification, two-way audio intervention, real-time threat assessment, sub-5-minute PRP coordinated emergency dispatch</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Case Study */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Mississauga Commercial Security: Case Study</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              A documented outcome from the GTAA cargo corridor
            </p>

            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-10">
                  <div className="text-sm font-semibold text-teal-600 mb-2 uppercase tracking-wide">GTAA Cargo Corridor — Freight Forwarder</div>
                  <h3 className="text-2xl font-bold mb-4">$340,000 in Cargo Losses. Zero Incidents After Installation.</h3>
                  <p className="text-gray-600 mb-6">
                    A bonded freight forwarder on Convair Drive experienced three separate cargo theft incidents totalling $340,000 in losses over eight months. Their cargo insurer issued a conditional non-renewal notice citing inadequate perimeter documentation and no verified dock access log.
                  </p>
                  <p className="text-gray-600 mb-6">
                    Alliance Security designed and installed a six-camera LPR perimeter system covering all dock doors and the main gate, integrated dock access control with Transport Canada-compliant audit logs, and connected the facility to our ULC-certified monitoring centre. The system was operational in 11 days.
                  </p>
                  <p className="text-gray-600">
                    Outcome: zero cargo theft incidents in the 10 months following installation. The insurer renewed the cargo policy at standard rates with a 26% premium reduction on the cargo warehouse endorsement. Two PRP Commercial Crime Unit investigation files were supported using our system&apos;s dock access logs and LPR footage.
                  </p>
                </div>
                <div className="relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20freight%20warehouse%20bonded%20facility%20dock%20doors%20GTAA%20cargo%20zone%20Mississauga%20Ontario%20industrial%20building%20professional%20security%20camera%20installation%20twilight%20lighting%20LPR%20perimeter&width=600&height=500&seq=mississauga-case-study-cargo&orientation=portrait"
                    alt="GTAA Cargo Corridor Security Case Study - Mississauga"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 divide-x divide-gray-200 bg-teal-600 text-white">
                <div className="p-6 text-center">
                  <div className="text-3xl font-bold mb-1">$340K</div>
                  <div className="text-sm text-teal-100">Losses — 3 incidents, 8 months</div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-3xl font-bold mb-1">11 days</div>
                  <div className="text-sm text-teal-100">Full system operational</div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-3xl font-bold mb-1">Zero</div>
                  <div className="text-sm text-teal-100">Incidents in 10 months post-install</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Neighborhoods Served */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Mississauga Business Districts We Serve</h2>
            <p className="text-center text-gray-600 mb-12">
              Protecting commercial properties across every Mississauga industrial zone, corporate corridor, and retail district
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {neighborhoods.map((neighborhood, index) => (
                <div key={index} className="bg-white px-6 py-4 rounded-lg text-center hover:bg-teal-50 transition-colors">
                  <div className="font-semibold text-gray-800">{neighborhood}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-4">Mississauga Commercial Security Resources</h2>
            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
              In-depth guides to security challenges specific to Mississauga&apos;s commercial zones
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <a href="/blog/commercial-security-company-mississauga" className="bg-gray-50 rounded-lg overflow-hidden group hover:shadow-lg transition-shadow cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=Mississauga%20commercial%20district%20Pearson%20Airport%20cargo%20zone%20Square%20One%20retail%20center%20aerial%20cityscape%20Peel%20Region%20Ontario%20business%20hub%20professional%20photography%20warm%20evening%20light&width=700&height=320&seq=mississauga-blog-resource-card&orientation=landscape"
                  alt="Best Commercial Security Company Mississauga - Guide"
                  className="w-full h-40 object-cover object-top"
                />
                <div className="p-6">
                  <div className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-2">Commercial Security Guide</div>
                  <h3 className="text-lg font-bold mb-2 group-hover:text-teal-600 transition-colors">Best Commercial Security Company Mississauga</h3>
                  <p className="text-gray-600 text-sm">Pearson cargo corridor theft rates, 401 industrial belt vulnerabilities, Square One ORC, and how to evaluate Mississauga security providers.</p>
                </div>
              </a>
              <a href="/service-areas/mississauga" className="bg-gray-50 rounded-lg overflow-hidden group hover:shadow-lg transition-shadow cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=Mississauga%20skyline%20residential%20commercial%20mixed%20district%20city%20center%20modern%20buildings%20aerial%20view%20Ontario%20Peel%20Region%20warm%20daylight%20urban%20landscape%20architecture&width=700&height=320&seq=mississauga-service-area-card&orientation=landscape"
                  alt="Mississauga Security Service Area"
                  className="w-full h-40 object-cover object-top"
                />
                <div className="p-6">
                  <div className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-2">Service Area</div>
                  <h3 className="text-lg font-bold mb-2 group-hover:text-teal-600 transition-colors">Mississauga Security Service Area</h3>
                  <p className="text-gray-600 text-sm">All residential and commercial security services available across Mississauga, from Port Credit to Meadowvale.</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Mississauga Commercial Security FAQs</h2>
            <p className="text-center text-gray-600 mb-12">
              Common questions from Mississauga business owners
            </p>

            <div className="space-y-6" itemScope itemType="https://schema.org/FAQPage">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white p-8 rounded-lg" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                  <h3 className="text-xl font-bold mb-3 flex items-start gap-3" itemProp="name">
                    <span className="text-teal-500 flex-shrink-0">Q:</span>
                    <span>{faq.question}</span>
                  </h3>
                  <div className="pl-8 text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p itemProp="text">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact-form" className="py-20 bg-white">
          <div className="max-w-2xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Get Your Free Mississauga Security Assessment</h2>
            <p className="text-center text-gray-600 mb-12">
              On-site evaluation, custom proposal, and transparent pricing — no obligation
            </p>

            <form
              className="space-y-6 bg-gray-50 p-8 rounded-lg"
              data-readdy-form
              id="mississauga-commercial-form"
              action="https://readdy.ai/api/form/d6ggpceqpmcju3gsd8n0"
              method="POST"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Business Name</label>
                  <input
                    type="text"
                    name="business_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Your Company Ltd."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Your Name</label>
                  <input
                    type="text"
                    name="contact_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="905-XXX-XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Business Address in Mississauga</label>
                <input
                  type="text"
                  name="business_address"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 Airport Rd, Mississauga, ON"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Industry</label>
                <select
                  name="industry"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select Industry</option>
                  <option value="cargo_freight">Cargo / Freight / Logistics</option>
                  <option value="retail">Retail</option>
                  <option value="office">Office / Corporate</option>
                  <option value="warehouse">Warehouse / Distribution</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="healthcare">Healthcare / Pharmaceutical</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="construction">Construction</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Security Needs (optional)</label>
                <textarea
                  name="security_details"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Describe your security concerns, current system, cargo insurance requirements, or specific needs..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">Maximum 500 characters</div>
              </div>

              <button
                type="submit"
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Free Assessment
              </button>
            </form>
          </div>
        </section>

        {/* Other Cities */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12">Commercial Security in Other GTA Cities</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {otherCities.map((city, index) => (
                <a
                  key={index}
                  href={`/commercial-security/${city.slug}`}
                  className="bg-white px-6 py-4 rounded-lg text-center hover:shadow-lg transition-shadow cursor-pointer group"
                >
                  <div className="font-semibold text-gray-800 group-hover:text-teal-600 transition-colors">{city.name}</div>
                </a>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="/commercial-security/service-areas" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center gap-2">
                View All Service Areas <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Strip */}
        <section className="bg-gradient-to-r from-teal-600 to-teal-700 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Mississauga Business?</h2>
            <p className="text-xl mb-8 text-white/90">
              Join 150+ Mississauga companies trusting us with their security — from GTAA cargo facilities to Square One retailers
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={scrollToForm}
                className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Assessment
              </button>
              <a
                href="/commercial-security"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                View All Solutions
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
